import cx from "classnames";
import styles from "modules/finances/widgets/month-plan-list/ui/financial-planning-row/financial-planning-row.module.scss";
import Link from "next/link";
import Router, { useRouter } from "next/router";
import pages from "pages/_router";
import React, { CSSProperties, useMemo } from "react";
import { api, MonthPlanSimpleDto } from "shared/api";
import { formatDate, pluralize } from "shared/lib";
import { Entity } from "shared/ui";

export interface IFinancialPlanningRow {
  companySlug: string;
  monthPlanList: Array<MonthPlanSimpleDto>;

  className?: string;
  style?: CSSProperties;
  htmlId?: string;
}

export const FinancialPlanningRow = (props: IFinancialPlanningRow) => {
  const { query } = useRouter();
  const companySlug = query.company as string;

  const financialPlans = api.financialPlan.useFinancialPlanList(companySlug);
  const draftFinancialPlan = financialPlans.data?.find((it) => it.status === "DRAFT" || it.status === "ON_APPROVAL");
  const draftMonthPlan = api.monthPlan.useMonthPlanGet(companySlug, draftFinancialPlan?.monthPlanId!);

  const monthPlanList = api.monthPlan.useMonthPlanList(companySlug);
  const approvedPlans = useMemo(
    () =>
      monthPlanList.data?.filter((it) => it.status === "APPROVED" || it.status === "APPROVED_AND_HAS_FINANCIAL_PLAN"),
    [monthPlanList.data]
  );

  const now = useMemo(() => new Date(), []);

  const expiredWeeks = useMemo(() => {
    const weeks = approvedPlans?.map((it) => it.weeks).flat();
    return weeks?.filter((it) => it.to < now && !it.closed).sort((a, b) => a.from.getTime() - b.from.getTime());
  }, [approvedPlans, now]);

  const onCreate = async () => {
    const resp = await api.financialPlan.financialPlanCreate(companySlug);
    await Router.push(pages.company(companySlug).finances.yearMonth(resp.year, resp.month).results.id(resp.id).href);
  };

  if (draftFinancialPlan && draftMonthPlan.data) {
    const { from, to } = draftFinancialPlan;
    const { year, month } = draftMonthPlan.data;

    return (
      <Entity className={cx(styles.financial_planning_row, props.className)} style={props.style} htmlId={props.htmlId}>
        Сейчас подводятся итоги {formatDate(from)} — {formatDate(to)}{" "}
        <Link href={pages.company(companySlug).finances.yearMonth(year, month).results.id(draftFinancialPlan.id).href}>
          Продолжить планирование
        </Link>
      </Entity>
    );
  }

  if (expiredWeeks?.length === 1) {
    const { from, to } = expiredWeeks[0];
    return (
      <Entity className={cx(styles.financial_planning_row, props.className)} style={props.style} htmlId={props.htmlId}>
        Пропущено планирование за {formatDate(from)} — {formatDate(to)} <a onClick={onCreate}>Провести планирование</a>
      </Entity>
    );
  }

  if (expiredWeeks && expiredWeeks.length! > 1) {
    const { from, to } = expiredWeeks[0];

    return (
      <Entity className={cx(styles.financial_planning_row, props.className)} style={props.style} htmlId={props.htmlId}>
        Пропущено{" "}
        {pluralize(expiredWeeks.length, {
          one: (it) => `${it} финансовое планирование`,
          few: (it) => `${it} финансовых планирования`,
          many: (it) => `${it} финансовых планирований`,
        })}{" "}
        <a onClick={onCreate}>
          Провести планирование за {formatDate(from)} — {formatDate(to)}{" "}
        </a>
      </Entity>
    );
  }

  return null;
};

FinancialPlanningRow.defaultProps = {};
